/**
 * The pure helpers behind the command palette's CMMD project flows.
 *
 * Extracted from `CommandPalette.tsx`, which is 2,700 lines against a 400-line
 * cap. None of this is React: it is URL building, id parsing, repo-name
 * normalisation and three shapes that turn a CMMD context into the
 * `CmmdProject` the palette hands onward. Keeping them in the component file
 * meant the one part of that flow a test can reach cheaply was buried in the
 * one part it cannot.
 */

import type {
  CmmdEnsuredGitHubProjectContext,
  CmmdProject,
  CmmdProjectContextLink,
} from "@cmmd-forge/contracts";

/** A CMMD project resolved for planning, however it was reached. */
export interface ResolvedCmmdPlanningContext {
  readonly project: CmmdProject;
  readonly organizationId: number;
  readonly campaignName: string | null;
  readonly source: "linked_existing" | "created_from_cmmd";
}

export function buildHttpUrlResolver(httpBaseUrl: string) {
  return (path: string, searchParams?: Record<string, string>) => {
    const url = new URL(httpBaseUrl);
    const basePath = url.pathname.endsWith("/") ? url.pathname.slice(0, -1) : url.pathname;
    url.pathname = `${basePath}${path.startsWith("/") ? path : `/${path}`}`;
    url.search = searchParams ? new URLSearchParams(searchParams).toString() : "";
    url.hash = "";
    return url.toString();
  };
}

export function parsePositiveCmmdId(value: string | number | null | undefined): number | null {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) && value > 0 ? value : null;
  }
  if (typeof value !== "string") return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

export function githubRepoNameFromProjectTitle(title: string, fallbackId: string): string {
  const normalized = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^[.-]+|[.-]+$/g, "")
    .replace(/-{2,}/g, "-");
  if (normalized.length > 0) return normalized.slice(0, 100);
  return `cmmd-project-${fallbackId}`.slice(0, 100);
}

export function sleep(ms: number): Promise<void> {
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms));
}

export function cmmdProjectFromLinkedContext(linkedContext: CmmdProjectContextLink): {
  readonly project: CmmdProject;
  readonly organizationId: number;
  readonly campaignName: string | null;
  readonly source: "linked_existing";
} {
  return {
    organizationId: linkedContext.organizationId,
    campaignName: null,
    source: "linked_existing",
    project: {
      id: String(linkedContext.projectId),
      title: linkedContext.projectTitle,
      status: "draft",
      campaignId:
        linkedContext.projectCampaignId !== null ? String(linkedContext.projectCampaignId) : null,
      workspaceId: String(linkedContext.organizationId),
      workspaceName: "CMMD",
      teamId: null,
      teamName: null,
      description: null,
      brief: null,
    },
  };
}

export function cmmdProjectFromExistingProject(
  project: CmmdProject,
  workspaceName: string,
  campaignName: string | null,
  fallbackOrganizationId: number | null,
): ResolvedCmmdPlanningContext | null {
  const projectId = parsePositiveCmmdId(project.id);
  // Cross-workspace CMMD projects arrive with a null workspaceId; fall back to the
  // caller's resolved project so Link doesn't silently fail (mirrors #1439's
  // parseRequiredCmmdProjectIdentity fallback for the CMMD-project source path).
  const organizationId = parsePositiveCmmdId(project.workspaceId) ?? fallbackOrganizationId;
  if (!projectId || !organizationId) return null;
  return {
    organizationId,
    campaignName,
    source: "linked_existing",
    project: {
      ...project,
      id: String(projectId),
      workspaceId: String(organizationId),
      workspaceName,
    },
  };
}

export function cmmdProjectFromEnsuredGitHubContext(
  ensured: CmmdEnsuredGitHubProjectContext,
  organizationId: number,
  workspaceName: string,
  campaignName: string | null,
  fallbackName: string,
): ResolvedCmmdPlanningContext {
  const cmmdProjectId = parsePositiveCmmdId(ensured.project.id);
  const cmmdProjectName =
    typeof ensured.project.title === "string" && ensured.project.title.trim().length > 0
      ? ensured.project.title.trim()
      : fallbackName;
  if (!cmmdProjectId) {
    throw new Error("CMMD returned a GitHub project context without a project id.");
  }
  return {
    organizationId,
    campaignName,
    source: ensured.source === "linked_existing" ? "linked_existing" : "created_from_cmmd",
    project: {
      id: String(cmmdProjectId),
      title: cmmdProjectName,
      status: "draft",
      campaignId: ensured.project.campaignId !== null ? String(ensured.project.campaignId) : null,
      workspaceId: String(organizationId),
      workspaceName,
      teamId: null,
      teamName: null,
      description: null,
      brief: null,
    },
  };
}
